import { useContext, useEffect, useState } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import AlertProvider from "../../context/AlertContext";
import { AuthContext, AuthProvider } from "../../context/AuthContext";
import AdminInfoProvider from "../../context/AdminInfoContext";
import useVerifyAuth from "../../hooks/useVerifyAuth";
import { useFeatureFlagToken } from "../../hooks/useFeatureFlag";
import { FeatureFlagName } from "../../types/feature-flags/featureFlag";
import { V2ThemeProvider } from "../theme/V2ThemeProvider";
import RailNav from "../components/RailNav/RailNav";
import ArchiePanel from "../components/ArchiePanel/ArchiePanel";
import CommandPalette from "../components/CommandPalette/CommandPalette";

const RAIL_STORAGE_KEY = "cr-v2-rail";

function V2Shell() {
  useVerifyAuth();

  const { user } = useContext(AuthContext);
  const { customerId } = useParams<{ customerId: string }>();
  const hasV2Flag = useFeatureFlagToken(FeatureFlagName.V2UI);

  const [railExpanded, setRailExpanded] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(RAIL_STORAGE_KEY);
      return stored !== null ? stored === "true" : false;
    } catch {
      return false;
    }
  });

  const [archieOpen, setArchieOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCmdOpen(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  if (!hasV2Flag && user !== null) {
    return <Navigate to={`/customers/${customerId}`} replace />;
  }

  const handleRailToggle = () => {
    setRailExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(RAIL_STORAGE_KEY, String(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  };

  const handleArchieToggle = () => {
    setArchieOpen((prev) => !prev);
  };

  return (
    <V2ThemeProvider>
      <div style={{ display: "flex", flexDirection: "row", height: "100vh" }}>
        <RailNav
          expanded={railExpanded}
          onToggle={handleRailToggle}
          onArchieToggle={handleArchieToggle}
        />
        <main
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden",
          }}
        >
          <Outlet />
        </main>
        {user?.hasFeatureFlag("Archie") && (
          <ArchiePanel open={archieOpen} onClose={handleArchieToggle} />
        )}
      </div>
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
    </V2ThemeProvider>
  );
}

export default function V2Root() {
  return (
    <AlertProvider>
      <AuthProvider>
        <AdminInfoProvider>
          <V2Shell />
        </AdminInfoProvider>
      </AuthProvider>
    </AlertProvider>
  );
}
